import { ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import PageErrorBoundary from './PageErrorBoundary'
import CardErrorBoundary from './CardErrorBoundary'

/**
 * NOTE: Props for PageErrorBoundaryWrapper
 */
interface PageErrorBoundaryWrapperProps {
    children: ReactNode
    className?: string
    onError?: (error: Error, errorInfo: { componentStack: string }) => void
    onReset?: () => void
}

/**
 * NOTE: Wrapper for ErrorBoundary for entire pages
 * Uses Sentry's native ErrorBoundary which automatically captures errors
 */
export const PageErrorBoundaryWrapper = ({
    children,
    className,
    onError,
    onReset,
}: PageErrorBoundaryWrapperProps) => {
    return (
        <Sentry.ErrorBoundary
            fallback={({ resetError, eventId }) => (
                <PageErrorBoundary
                    className={className}
                    resetErrorBoundary={resetError}
                    sentryEventId={eventId}
                />
            )}
            beforeCapture={(scope) => {
                scope.setTag('errorBoundary', true)
                scope.setTag('boundaryType', 'page')
            }}
            onError={onError as any}
            onReset={onReset}
        >
            {children}
        </Sentry.ErrorBoundary>
    )
}

/**
 * NOTE: Props for CardErrorBoundaryWrapper
 */
interface CardErrorBoundaryWrapperProps {
    children?: ReactNode
    className?: string
    title?: string
    description?: string
    onError?: (error: Error, errorInfo: { componentStack: string }) => void
    onReset?: () => void
}

/**
 * NOTE: Wrapper for ErrorBoundary for specific cards/components
 * Uses Sentry's native ErrorBoundary which automatically captures errors
 */
export const CardErrorBoundaryWrapper = ({
    children,
    className,
    title,
    description,
    onError,
    onReset,
}: CardErrorBoundaryWrapperProps) => {
    return (
        <Sentry.ErrorBoundary
            fallback={({ resetError, eventId }) => (
                <CardErrorBoundary
                    className={className}
                    title={title}
                    description={description}
                    resetErrorBoundary={resetError}
                    sentryEventId={eventId}
                />
            )}
            beforeCapture={(scope) => {
                scope.setTag('errorBoundary', true)
                scope.setTag('boundaryType', 'card')
            }}
            onError={onError as any}
            onReset={onReset}
        >
            {children}
        </Sentry.ErrorBoundary>
    )
}